import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useNotifications } from '../contexts/NotificationContext.jsx';

const TABS = [
  { key: 'all',       label: 'All',        filter: () => true },
  { key: 'unread',    label: 'Unread',     filter: (n) => !n.is_read && !n.read_at },
  { key: 'issue',     label: 'Issues',     filter: (n) => n.category === 'issue' },
  { key: 'approval',  label: 'Approvals',  filter: (n) => n.category === 'approval' },
  { key: 'mention',   label: 'Mentions',   filter: (n) => n.category === 'mention' },
  { key: 'system',    label: 'System',     filter: (n) => n.category === 'system' },
];

const CATEGORY_ICON = {
  issue:       { color: '#2563eb', glyph: '✓' },
  approval:    { color: '#16a34a', glyph: '✔' },
  mention:     { color: '#f59e0b', glyph: '@' },
  leave:       { color: '#0ea5e9', glyph: '🏖' },
  payroll:     { color: '#9333ea', glyph: '$' },
  wiki:        { color: '#0d9488', glyph: '📄' },
  procurement: { color: '#db2777', glyph: '📦' },
  qa:          { color: '#dc2626', glyph: '🐞' },
  comment:     { color: '#64748b', glyph: '💬' },
  system:      { color: '#475569', glyph: '!' },
};

export default function NotificationBell() {
  const { items, unread, byCategory, pulse, markRead, markAllRead, removeOne } = useNotifications();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('all');
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    function onClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    function onKey(e) { if (e.key === 'Escape') setOpen(false); }
    if (open) {
      document.addEventListener('mousedown', onClick);
      document.addEventListener('keydown', onKey);
    }
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const fn = TABS.find((t) => t.key === tab)?.filter || (() => true);
    return items.filter(fn);
  }, [items, tab]);

  const groups = useMemo(() => groupByTime(filtered), [filtered]);

  function handleItemClick(n) {
    if (!n.is_read && !n.read_at) markRead(n.id);
    if (n.resource_url) {
      setOpen(false);
      navigate(localizeUrl(n.resource_url));
    }
  }

  return (
    <div className="bell" ref={ref} style={{ position: 'relative' }}>
      <button
        className={`bell-btn${pulse ? ' pulse' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-label="Notifications"
        aria-expanded={open}
        data-testid="notification-bell"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unread > 0 && (
          <span className="bell-badge" data-testid="notification-badge">{unread > 99 ? '99+' : unread}</span>
        )}
      </button>

      {open && (
        <div className="bell-dropdown card" data-testid="notification-dropdown" style={{
          position: 'absolute', right: 0, top: 'calc(100% + 6px)',
          width: 380, maxHeight: 520, display: 'flex', flexDirection: 'column',
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
          boxShadow: '0 8px 24px rgba(15,23,42,0.12)', zIndex: 200,
        }}>
          <header style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderBottom: '1px solid #f1f5f9' }}>
            <strong style={{ flex: 1 }}>Notifications</strong>
            {unread > 0 && (
              <button className="btn ghost small" onClick={markAllRead} data-testid="bell-mark-all">Mark all read</button>
            )}
            <Link to="/profile/notifications/settings" onClick={() => setOpen(false)} aria-label="Notification settings"
                  data-testid="bell-settings"
                  style={{ color: '#475569', textDecoration: 'none', padding: 4 }}>⚙</Link>
          </header>

          <nav style={{ display: 'flex', overflowX: 'auto', padding: '6px 8px', gap: 4, borderBottom: '1px solid #f1f5f9' }}>
            {TABS.map((t) => {
              const count = t.key === 'unread'
                ? unread
                : t.key === 'all'
                  ? items.length
                  : (byCategory[t.key] || items.filter(t.filter).length);
              return (
                <button
                  key={t.key}
                  data-testid={`bell-tab-${t.key}`}
                  onClick={() => setTab(t.key)}
                  className={`bell-tab${tab === t.key ? ' active' : ''}`}
                  style={{
                    border: 'none', background: tab === t.key ? '#e0f2fe' : 'transparent',
                    color: tab === t.key ? '#0369a1' : '#475569',
                    fontSize: 12, padding: '4px 10px', borderRadius: 999,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                  }}>
                  {t.label}
                  {count > 0 && <span style={{ fontSize: 10, padding: '0 5px', borderRadius: 8, background: tab === t.key ? '#0369a1' : '#cbd5e1', color: '#fff' }}>{count}</span>}
                </button>
              );
            })}
          </nav>

          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filtered.length === 0 && (
              <div className="muted center small" style={{ padding: 24 }} data-testid="bell-empty">
                {tab === 'all' ? "You're all caught up! 🎉" : `No ${TABS.find((t) => t.key === tab).label.toLowerCase()} notifications`}
              </div>
            )}
            {groups.map((g) => (
              <section key={g.label}>
                <div style={{ padding: '8px 12px 4px', fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.05em' }}>{g.label}</div>
                {g.items.map((n) => (
                  <NotifRow key={n.id} n={n} onClick={() => handleItemClick(n)} onDismiss={() => removeOne(n.id)} />
                ))}
              </section>
            ))}
          </div>

          <footer style={{ padding: '8px 12px', borderTop: '1px solid #f1f5f9', textAlign: 'center' }}>
            <Link to="/profile/notifications" onClick={() => setOpen(false)} className="muted small" data-testid="bell-view-all">
              View all notifications
            </Link>
          </footer>
        </div>
      )}
    </div>
  );
}

function NotifRow({ n, onClick, onDismiss }) {
  const ci = CATEGORY_ICON[n.category] || CATEGORY_ICON.system;
  const isUnread = !n.is_read && !n.read_at;
  return (
    <div
      data-testid={`notification-item-${n.id}`}
      data-unread={isUnread ? 'true' : 'false'}
      onClick={onClick}
      style={{
        display: 'flex', gap: 10, padding: '10px 12px',
        borderBottom: '1px solid #f8fafc', cursor: 'pointer',
        background: isUnread ? '#f0f9ff' : '#fff',
        position: 'relative',
      }}
    >
      <div style={{ position: 'relative', width: 32, flexShrink: 0 }}>
        <div style={{
          width: 32, height: 32, borderRadius: 16,
          background: '#e2e8f0', color: '#334155',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 13, fontWeight: 600,
        }}>
          {(n.actor_name || n.actor_email || '?').slice(0, 1).toUpperCase()}
        </div>
        <div style={{
          position: 'absolute', right: -2, bottom: -2,
          width: 14, height: 14, borderRadius: 7,
          background: ci.color, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 9, fontWeight: 700, border: '2px solid #fff',
        }}>{ci.glyph}</div>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: isUnread ? 600 : 500, color: isUnread ? '#0f172a' : '#475569', lineHeight: 1.3 }}>
          {n.title}
        </div>
        {n.body && <div className="muted small" style={{ marginTop: 2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{n.body}</div>}
        <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
          {n.metadata?.issue_key && <span className="chip" style={{ fontSize: 10 }}>{n.metadata.issue_key}</span>}
          <span className="muted" style={{ fontSize: 11 }}>{relTime(n.created_at)}</span>
        </div>
      </div>
      {isUnread && <div style={{ width: 8, height: 8, borderRadius: 4, background: '#3b82f6', flexShrink: 0, marginTop: 12 }} aria-label="Unread" />}
      <button
        type="button"
        aria-label="Dismiss"
        onClick={(e) => { e.stopPropagation(); onDismiss(); }}
        style={{ position: 'absolute', top: 6, right: 6, border: 'none', background: 'transparent', color: '#94a3b8', cursor: 'pointer', fontSize: 14, padding: 2 }}
      >×</button>
    </div>
  );
}

function localizeUrl(u) {
  if (!u) return '#';
  if (u.startsWith('http')) return u;
  return u;
}

function groupByTime(items) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const yest = new Date(today); yest.setDate(yest.getDate() - 1);
  const week = new Date(today); week.setDate(week.getDate() - 7);
  const groups = { Today: [], Yesterday: [], 'This week': [], Earlier: [] };
  for (const n of items) {
    const d = new Date(n.created_at);
    if (d >= today) groups.Today.push(n);
    else if (d >= yest) groups.Yesterday.push(n);
    else if (d >= week) groups['This week'].push(n);
    else groups.Earlier.push(n);
  }
  return Object.entries(groups)
    .filter(([, items]) => items.length > 0)
    .map(([label, items]) => ({ label, items }));
}

function relTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
